import { useMemo } from 'react'
import { useFormContext, useFormState, get } from 'react-hook-form'
import FormControl from '@mui/material/FormControl'
import FormGroup from '@mui/material/FormGroup'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import { arrayOf, bool, node, oneOf, oneOfType, shape, string } from 'prop-types'
import ControlledCheckbox from './controlled-checkbox'

const ControlledCheckboxGroup = ({
	alignment,
	className,
	fullWidth,
	helperText,
	items,
	label,
	name,
	required,
	row,
}) => {
	const formContext = useFormContext()
	const isSubmitting = formContext?.formState?.isSubmitting
	const getValues = formContext?.getValues

	const { errors } = useFormState({ name, exact: true })
	const groupError = get(errors, name)
	const needDefaults = useMemo(() => !getValues(name), [getValues, name])

	const singleCheckboxesErrors = items
		.map(({ name: itemName }) => get(errors, `${name}.${itemName}`))
		.filter((err) => !!err)

	return (
		<FormControl
			component="fieldset"
			required={required}
			error={!!groupError}
			fullWidth={!!fullWidth}
			className={className}
		>
			{label && <FormLabel>{label}</FormLabel>}
			<FormGroup row={!!row}>
				{items.map(({ additionalContent = null, defaultChecked, ...item }) => (
					<div key={item.id}>
						<ControlledCheckbox
							{...item}
							name={`${name}.${item.name}`}
							alignment={alignment}
							groupName={name}
							noError
							defaultChecked={needDefaults ? defaultChecked : undefined}
							disabled={item?.disabled ?? isSubmitting}
						/>
						{additionalContent}
					</div>
				))}
			</FormGroup>
			{groupError || singleCheckboxesErrors.length > 0 ? (
				<>
					{groupError?.message && <FormHelperText error>{groupError?.message}</FormHelperText>}
					{singleCheckboxesErrors.map(
						(error, i) =>
							error?.message && (
								<FormHelperText key={i} error>
									{error.message}
								</FormHelperText>
							)
					)}
				</>
			) : helperText ? (
				<FormHelperText>{helperText}</FormHelperText>
			) : null}
		</FormControl>
	)
}

ControlledCheckboxGroup.defaultProps = {
	alignment: 'center',
	required: false,
	row: false,
	helperText: ' ',
}

ControlledCheckboxGroup.propTypes = {
	/**
	 * Alignment of the label to the checkbox.
	 */
	alignment: oneOfType([oneOf(['start', 'center', 'end']), string]),
	/**
	 * ControlledCheckboxGroup helper text.
	 */
	helperText: oneOfType([string, node]),
	/**
	 * Form label.
	 */
	label: node,
	/**
	 * Checkbox items.
	 */
	items: arrayOf(
		shape({
			...ControlledCheckbox.propTypes,
		})
	).isRequired,
	/**
	 * Form group name.
	 */
	name: string.isRequired,
	/**
	 * If true, the label will indicate that the input is required.
	 */
	required: bool,
	/**
	 * Display group of elements in a compact row.
	 */
	row: bool,
}

export default ControlledCheckboxGroup
